export const ASC = 'asc';
export const DESC = 'desc';

/**
 * Sorts an array of objects by multiple keys and directions.
 *
 * @param {Array.<Object>} arr - The array of objects to be sorted.
 * @param {Array.<Array>} keys - An array of key-direction pairs for sorting.
 * @returns {Array.<Object>} The sorted array.
 * @throws {Error} Throws an error if 'arr' or 'keys' is not an array.
 *
 * @example
 * // Sort 'data' array by 'age' in ascending order and then by 'name' in descending order
 * const data = [
 *   { name: 'Alice', age: 30 },
 *   { name: 'Bob', age: 25 },
 *   { name: 'Charlie', age: 35 },
 * ];
 * const sortedData = sortByKey(data, [['age', ASC], ['name', DESC]]);
 */
export const sortByKey = (arr = [], keys = []) => {
  if (!Array.isArray(arr) || !Array.isArray(keys)) {
    throw new Error('Both arr and keys must be arrays');
  }
  arr.sort((a, b) => {
    keys = keys.reverse();
    let totalScore = 0;
    for (let i = 0; i < keys.length; i++) {
      const [key, direction] = keys[i];
      let score = Math.pow(10, i);
      if (direction === DESC) {
        score *= -1;
      }
      if (getNestedValue(a, key) > getNestedValue(b, key)) {
        totalScore += score;
      } else if (a[key] < b[key]) {
        totalScore -= score;
      }
    }
    return totalScore;
  });

  return [...arr];
};

/**
 * Retrieves the value of a nested property based on a dot-separated key.
 *
 * @param {Object} obj - The object to traverse.
 * @param {String} key - The dot-separated key (e.g., "level1.level2").
 * @returns {*} The value of the nested property or undefined if not found.
 */
export const getNestedValue = (obj, key) => {
  return key.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
};
